import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import {
    AgencyClientRow,
    AgencyDashboardPeriod,
} from '@/integrations/supabase/rpc-types';

interface UseAgencyClientsParams {
    /** Controllato dal padre: e' lo stesso selettore delle altre tab della pagina. */
    period: AgencyDashboardPeriod;
    designerId: string | null;
    search: string;
}

interface UseAgencyClientsResult {
    data: AgencyClientRow[];
    loading: boolean;
    /** Vero solo dai ricaricamenti successivi: la lista resta a schermo mentre arriva. */
    refreshing: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

/** Ritardo del debounce sulla ricerca, in ms. */
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Lista clienti della tab Clienti.
 *
 * La ricerca e' filtrata dal database e non in memoria: la lista non e' paginata ma puo'
 * essere lunga, e soprattutto cerca anche fra le destinazioni, che nella riga sono
 * troncate e quindi non tutte visibili al filtro del browser.
 *
 * Il debounce sta qui e non nel componente perche' e' una proprieta' della chiamata, non
 * del campo di testo: chi usa l'hook passa il valore corrente e non deve saperne nulla.
 */
export function useAgencyClients({
    period,
    designerId,
    search,
}: UseAgencyClientsParams): UseAgencyClientsResult {
    const [data, setData] = useState<AgencyClientRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { t } = useI18n();

    // Distingue il primo caricamento dai successivi: al primo si mostra lo scheletro, dopo
    // si tiene la lista vecchia sotto un velo, altrimenti ogni lettera digitata farebbe
    // sparire la tabella.
    const loadedOnce = useRef(false);

    const fetchData = useCallback(async () => {
        if (loadedOnce.current) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            const result = await AgencyDashboardService.getClients(
                period,
                designerId,
                search
            );
            setData(result);
            loadedOnce.current = true;
        } catch {
            const msg = String(t('errors.loadFailed'));
            setError(msg);
            toast({
                title: String(t('common.error')),
                description: msg,
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, designerId, search]);

    useEffect(() => {
        // Periodo e advisor cambiano con un click e vanno subito; la ricerca si scrive una
        // lettera per volta, quindi il timer serve solo a lei. Tenerlo unico costa al
        // massimo un ritardo impercettibile sui due filtri a click.
        const timer = setTimeout(fetchData, search.trim() ? SEARCH_DEBOUNCE_MS : 0);
        return () => clearTimeout(timer);
    }, [fetchData, search]);

    return { data, loading, refreshing, error, refresh: fetchData };
}

export default useAgencyClients;
